import React, { useEffect, useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl, isNotEmpty } from '../../lib/ep.js';
import { loadTokens } from '../../reducers/loyalty_tokens.js';
import { useSelector } from 'react-redux';

import BuilderCanvas from '../builder/BuilderCanvas';
import BuilderSidebar from '../builder/BuilderSidebar';
import Model from '../builder/Model';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';

import SectionTitle from '../builder/SectionTitle.js';
import SectionSubtitle from '../builder/SectionSubtitle.js';
import BuilderMain from '../builder/BuilderMain.js';
import DynamicFields from '../builder/DynamicFields.js';
import Input from '../builder/Input.js';
import Select from '../builder/Select';
import { formatTokenTitle } from '../front/loyalty/TokenAmount.js';
import SegmentBuilder from './elements/SegmentBuilder.js';
import { get_all_validators } from '../../lib/tag_validators.js';

const LoyaltyAward = ({ model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	let conditions = [
		{ k:'', v:`-- ${i18n("Pick condition")} --` },
		...props.conditions
	];

	if(model.type == 'ticket_ocr_total') {
		conditions =[
			{ k:'', v:`-- ${i18n("Pick condition")} --` },
			{ k:'between', v:i18n("Between") },
			{ k:'>', v:i18n("Greater than") },
			{ k:'for_every', v:i18n("For every") },
		];
	}

	if (model.type == 'game_completion') {
		conditions =[
			{ k:'', v:`-- ${i18n("Pick condition")} --` },
			{ k:'yes', v:i18n("Game completed") },
			{ k:'no', v:i18n("Game not completed") },
		];
	}

	const ticket_ocr = ['ticket_ocr_total','ticket_amount_tag','ticket_quantity_tag'];
	const ticket_ocr_tag = ['ticket_amount_tag','ticket_quantity_tag'];

	const segment_validations = [
		{ k:'', v:`-- ${i18n("No segment conditions")} --` },
		{ k:'has', v:i18n("Has one or more segments") },
		{ k:'has_not', v:i18n("Has none of this segments") }
	];

	let highest_plan = window?.type_in_plan ?? false;
	let is_corporate = window?.account_corporate ?? false;
	const has_min_plan = (window.has_subscription && ((highest_plan !== false && highest_plan == '2') || is_corporate)) ? true : false;

	const setSegment = (segment_id) => {
		if (model.segments == undefined) model.segments = [];
		if(model.segments.includes(segment_id)) {
			model.segments = model.segments.filter((s) => s != segment_id);
			if(model.segments.length == 0) model.segments = "";
		} else {
			model.segments = [...model.segments,segment_id];
		}
		change('segments', model.segments);
	}

	return (
		<Model
			model={model}
			change={change}
			errors={props.errors != undefined && props.errors[model.id]}
		>
			{(ticket_ocr_tag.includes(model.type)) && (
				<Select
					name="tag"
					values={[{ k:"", v:`-- ${i18n("Select")} --` }].concat(props.validator_tags.filter((t) => (t.OcrTag.type == 'article')).map((t)=>({ k:t.OcrTag.id,v:t.OcrTag.name })))}
					_class={{ size:60 }}
				>Condition</Select>
			)}

			{(model.type == 'ticket_tag_presence') && (
				<Select
					name="tag"
					values={[{ k:"", v:`-- ${i18n("Select")} --` }].concat(props.validator_tags.map((t) => ({ k:t.OcrTag.id, v:t.OcrTag.name })))}
					_class={{ size:60 }}
				>Condition</Select>
			)}

			{(model.type != 'loyalty_award' && model.type != 'ticket_tag_presence') && (
				<Select
					name="condition"
					values={conditions}
					_class={{ size:60 }}
				>Condition</Select>
			)}

			{(ticket_ocr.includes(model.type) && model.condition != 'between') && (
				<Input 
					name="threshold"
					value={model?.threshold ?? '0'}
					_type='number'
					_class={{ size:30 }}
				>Value</Input>
			)}

			{(model.condition == 'between') && (
				<Input 
					name="lower_threshold"
					value={model?.lower_threshold ?? '0'}
					_type='number'
					_class={{ size:30 }}
				>From</Input>
			)}

			{(model.condition == 'between') && (
				<Input 
					name="higher_threshold"
					value={model?.higher_threshold ?? '0'}
					_type='number'
					_class={{ size:30 }}
				>To</Input>
			)}

			{(model.type == 'loyalty_score' && model.condition != 'between') && (
				<Input 
					name="threshold"
					value={model?.threshold ?? '0'}
					_type='number'
					_class={{ size:30 }}
				>Value</Input>
			)}

			<Input
				name="amount"
				//sided_legend={props.tokens[0].ref}
				value={model?.amount ?? '0'}
				_type='number'
				_class={{ size:30 }}
			>Amount of coins to give</Input>

			<Select
				name="segment_validation"
				values={segment_validations}
			>Segment conditions</Select>

			{(isNotEmpty(model, 'segment_validation')) && (
				<SegmentBuilder
					segmentSelected={setSegment}
					promotion_id={props.promotion_id}
					selected={model?.segments ?? []}
				/>
			)}
		</Model>
	)
};

const Wrapper = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	return (
		<BuilderMain pm={props?.promotion ?? {}}>
			{props.children}

			<BuilderCanvas is_adaptable={false} add_public_css_vars={false}>
				<div class="not_awesome gui_is_padded">
					<SectionTitle _class={{ icon:'knk-filter', first:true }}>Coin allocation rules</SectionTitle>

					<p>{i18n("There are 2 types of rules")}:</p>

					<ol>
						<li class="gui_not_in_top">
							<p><strong>{i18n("On participate: Allows you to award X virtual coins for completing a participation.")}</strong></p>

							<p><u>{i18n("Examples")}:</u></p>

							<ul class="bulleted_list gui_not_in_top_tiny">
								<li>{i18n("Awarding 20 coins for uploading a valid purchase receipt.")}</li>

								<li>{i18n("Awarding 20 coins for entering a valid code.")}</li>
							</ul>
						</li>

						<li class="gui_not_in_top">
							<p><strong>{i18n("By score: Allows you to award X virtual coins for the points earned in the participation.")}</strong></p>

							<p><u>{i18n("Examples")}:</u></p>

							<ul class="bulleted_list gui_not_in_top_tiny">
								<li>{i18n("Minimum: awarding 10 coins for surpassing 300 points in a game.")}</li>

								<li>{i18n("Per each: awarding 1 coin for every 100 points earned in a game.")}</li>

								<li>{i18n("Range: awarding 5 coins if the user scores between 400 and 500 points.")}</li>
							</ul>
						</li>
					</ol>

					<div class="gui_not_in_top_big">
						<p><strong>{i18n("Multiple rules")}:</strong></p>

						<p class="gui_not_in_top_tiny">{i18n("In case of creating multiple rules, the coins that meet each rule will be given.")}</p>

						<p class="gui_not_in_top_tiny">{i18n("For example: if you give 3 coins for participating, and 5 coins for surpassing 100 points in a game, if a user plays and scores 150 points, they will earn 8 coins.")}</p>
					</div>
				</div>
			</BuilderCanvas>
		</BuilderMain>
	)
};

const StageLoyaltyBuilder = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stage = props?.stage ?? false;
	const promotion = props?.promotion ?? false;

	const [ state, setState ] = useState({ errors:{} });
	const [validator_tags,setValidatorTags] = useState([]);

	useEffect(()=>{
		get_all_validators(promotion.account_id,setValidatorTags);
	},[])
	
	const loyalty_change = (v) => {
		props.change('loyalty_awards', v);
	};

	const tokens_store = useSelector((state) => state.tokens);

	const tokens = useMemo(() => {
		if(tokens_store == false) return [];
		return tokens_store.tokens.map((token) => ({
			k:token.LoyaltyToken.id,
			v:token.LoyaltyToken.title,
		}))
	}, [ tokens_store ]);

	if(stage === false || promotion === false) return null;

	const conditions = [
		{ k:'=', v:i18n("Equal to") },
		{ k:'<', v:i18n("Less than") },
		{ k:'between', v:i18n("Between") },
		{ k:'>', v:i18n("Greater than") },
		{ k:'for_every', v:i18n("For every") },
	];

	const dynamic_fields = [{
		type:'loyalty_award',
		label:"Upon participating",
		add:(f) => {
			return {
				token:tokens[0].k,
				condition:'direct'
			}
		},
		title:(f) => {
			const token = tokens_store?.tokens?.find((t) => (t.LoyaltyToken.id == f.token));

			let given_amount = 0;
			if(isNotEmpty(f, 'amount') && !isNaN(f.amount)) given_amount = f.amount;

			return props.intl.formatMessage({
				id:"Give {amount} {action}",
			}, {
				amount:`${given_amount} ${formatTokenTitle(token.LoyaltyToken, given_amount)}`,
				action:i18n("Upon participating", 'lowercase'),
			});
		},
	}] 
	
	if (stage.is_game || stage.is_new_game || stage.type == '2' || stage.type == '5') {
		dynamic_fields.push({
			type:'game_completion',
			label:"Upon Completion",
			add:(f) => {
				return {
					token:tokens[0].k,
				}
			},
			title:(f) => {
				return i18n("Give coins based on completion");
			},
		})
	}
	
	dynamic_fields.push({
		type:'loyalty_score',
		label:"By score",
		add:(f) => {
			return {
				token:tokens[0].k,
			}
		},
		title:(f) => {
			return i18n("Give coins based on score");
		},
	})

	if (stage.reqs_type == '8') {

		
		dynamic_fields.push({
			type:'ticket_ocr_total',
			label:`${i18n("Receipt IA")} - ${i18n("Total amount")}`,
			add:(f) => {
				return {
					token:tokens[0].k,
				}
			},
			title:(f) => {
				return i18n("Coins for total amount of receipt");
			},
		})
		dynamic_fields.push({
			type:'ticket_tag_presence',
			label:`${i18n("Receipt IA")} - ${i18n("***Tag")}`,
			add:(f) => {
				return {
					token:tokens[0].k,
				}
			},
			title:(f) => {
				return i18n("Coins for specific tag");
			},
		});
		
		dynamic_fields.push({
			type:'ticket_amount_tag',
			label:`${i18n("Receipt IA")} - ${i18n("***Tag")} - ${i18n("Article amount")}`,
			add:(f) => {
				return {
					token:tokens[0].k,
				}
			},
			title:(f) => {
				return i18n("Coins for amount spent on article");
			},
		}) 
		
		dynamic_fields.push({
			type:'ticket_quantity_tag',
			label:`${i18n("Receipt IA")} - ${i18n("***Tag")} - ${i18n("Article quantity")}`,
			add:(f) => {
				return {
					token:tokens[0].k,
				}
			},
			title:(f) => {
				return i18n("Coins for quantity of the article");
			},
		});
	}

	return (
		<Wrapper promotion={props?.pm ?? {}} intl={props.intl}>
			<BuilderSidebar
				onSave={(cb) => props.saveStage(cb)}
				errors={{ ...props.errors, ...state.errors }}
			>
				<NavigationTabsBuilder
					sections={props.nav_sections}
					active={props.nav_active}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<DynamicFields
						section_title="Rules"
						section_icon='knk-filter'
						add_field_label="Add rule"
						onChange={loyalty_change}
						sortable={false}
						fields={stage.loyalty_awards ?? []}
						field_types={dynamic_fields}
					>
						<LoyaltyAward
							promotion_id={promotion.id}
							stage_id={stage.id}
							tokens={tokens}
							conditions={conditions}
							validator_tags={validator_tags}
							intl={props.intl}
						/>
					</DynamicFields>
				</article>
			</BuilderSidebar>
		</Wrapper>
	)
};

export default injectIntl(StageLoyaltyBuilder);